import * as Yup from 'yup';
import { useState, useEffect, useContext } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { createRoutesFromChildren, useNavigate, useParams } from 'react-router-dom';
import { mask } from "remask";
import NumberFormat from 'react-number-format';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// material
import { Stack, TextField, Alert, Autocomplete} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ThemeProvider } from '@mui/material/styles';
import Theme from '../../../components/Button';

import {api, reiniciarToken, consultaProdutos, cadastrarVendasMesa} from '../../../services/api';
// context
import { AuthContext } from '../../../contexts/auth';

import {fDateFormatCalendario} from '../../../utils/formatTime';
import {fFormataDinheiroSemCifra} from '../../../utils/formatNumber';

// ----------------------------------------------------------------------

export default function CadVendaForm() {
  
  const {id} = useParams();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [status, setStatus] = useState({
    type: '',
    mensagem: ''
  })
  
  const [produtos, setListProduto] = useState([]);

  const getListas = async () => {
    
    const responseProduto = await consultaProdutos();

    if(responseProduto.erro === true) {
      if (responseProduto.mensagem === 'O Token é inválido!' || responseProduto.mensagem === 'Acesso negado!'){
        logout();
      }
    }else {
      responseProduto.data.forEach((valores) => {
        if (valores.movimenta_estoque === 1 && valores.qtde_estoque > 0) {
          setListProduto(produtos => [...produtos, {id_produto: valores.id_produto, descricao: valores.descricao, preco_custo: valores.preco_venda_inicial ? valores.preco_venda_inicial : valores.preco_custo}])
        }else if (valores.movimenta_estoque === null || valores.movimenta_estoque === 0) {
          setListProduto(produtos => [...produtos, {id_produto: valores.id_produto, descricao: valores.descricao, preco_custo: valores.preco_venda_inicial ? valores.preco_venda_inicial : valores.preco_custo}])
        }
      });
    };
  }

  useEffect(() => {
    
    const renovarToken = async () => {
      const retorno = await reiniciarToken();
      if(retorno.erro === true) {
        if (retorno.mensagem === 'O Token é inválido!' || retorno.mensagem === 'Acesso negado!'){
          logout();
        }
      }else{
        localStorage.setItem("token", retorno.data.token);
        api.defaults.headers.Authorization = `Bearer ${retorno.data.token}`;
      }
    };

    renovarToken();
    getListas();

  },[]);

  const RegisterSchema = Yup.object().shape({
    id_produto: Yup.string().required('Produto/Serviço é obrigatório'),
    qtde_venda: Yup.string().required('Qtde de Venda é obrigatório'),
    valor_venda: Yup.string().required('Valor da Venda é obrigatório'),
    preco_custo: Yup.string().required('Preço de Custo é obrigatório'),
  });

  const formik = useFormik({
   initialValues: {
        id_produto: '',    
        qtde_venda: '',
        preco_custo: 0,
        valor_venda: 0,
        observacao: '',
    },
    validationSchema: RegisterSchema,
    onSubmit: () => {
      navigate('/dashboard', { replace: true });
    },
  });

  const handleSubmit = async e => {
    e.preventDefault();

    const retorno = await reiniciarToken();
    if(retorno.erro === true) {
      if (retorno.mensagem === 'O Token é inválido!' || retorno.mensagem === 'Acesso negado!'){
        logout();
      }
    }else{
      localStorage.setItem("token", retorno.data.token);
      api.defaults.headers.Authorization = `Bearer ${retorno.data.token}`;
    }
    
    const json = {id_produto: formik.values.id_produto,
                  qtde_venda: formik.values.qtde_venda,
                  preco_custo: formik.values.preco_custo,
                  valor_venda: formik.values.valor_venda,
                  data_venda: Date.now(),
                  observacao: formik.values.observacao,
                  usuario: localStorage.getItem("usuario"), 
                  id_empresa: localStorage.getItem("id_empresa"),
                  id_mesa: id,
                  situacao: 1};
                  
    const response = await cadastrarVendasMesa(json);

    if(response.erro === true) {
      if (response.mensagem === 'O Token é inválido!'){
        logout();
      }else {
        setStatus({
          type: "erro",
          mensagem: response.mensagem
        })
      }
    }else {
      setStatus({
        type: "success",
        mensagem: response.data.mensagem
      })
      if (id){
          navigate(`/venda_comercio/consultar/${id}`);
      } else{
          navigate(`/venda_avulsa/consultar`);
      };  
    };
  }

  const { errors, touched, isSubmitting, getFieldProps, handleBlur, handleChange, setFieldValue} = formik;
 
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Autocomplete
                getOptionLabel={(resultado) => resultado.descricao}
                options={produtos}
                fullWidth
                onChange={(e, value) => { 
                  setFieldValue('id_produto', value?.id_produto, false) 
                  setFieldValue('preco_custo', fFormataDinheiroSemCifra(value?.preco_custo), false)
                }}
                renderInput={(params) => (<TextField {...params} 
                                            label="Produto/Serviço" 
                                            fullWidth
                                            {...getFieldProps('id_produto')}
                                            error={Boolean(touched.id_produto && errors.id_produto)}
                                            helperText={touched.id_produto && errors.id_produto}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={formik.values?.id_produto}
                                            />
                                          )}
                isOptionEqualToValue={(option, value) => option.id_produto === value.id_produto}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Qtde Venda"
              {...getFieldProps('qtde_venda')}
              error={Boolean(touched.qtde_venda && errors.qtde_venda)}
              helperText={touched.qtde_venda && errors.qtde_venda}
              onChange={(ev) => {
                const valorFormatado = formik.values?.preco_custo.replace('.',"").replace(',','.')
                const valorVenda = (ev.target.value * valorFormatado)
                setFieldValue('qtde_venda', ev.target.value, false) 
                setFieldValue('valor_venda', valorVenda)
              }}
            />
            <NumberFormat
              fullWidth
              label="Preço de Venda"
              {...getFieldProps('preco_custo')}
              error={Boolean(touched.preco_custo && errors.preco_custo)}
              helperText={touched.preco_custo && errors.preco_custo}
              customInput={TextField}
              decimalSeparator={','}
              onChange={(ev) => {
                const valorFormatado = ev.target.value.replace('.',"").replace(',','.')
                const valorVenda = (formik.values?.qtde_venda * valorFormatado)
                setFieldValue('preco_custo', ev.target.value, false)
                setFieldValue('valor_venda', valorVenda)
              }}
            />
            <NumberFormat
              fullWidth
              label="Total Venda"
              {...getFieldProps('valor_venda')}
              error={Boolean(touched.valor_venda && errors.valor_venda)}
              helperText={touched.valor_venda && errors.valor_venda}
              customInput={TextField}
              decimalSeparator={','}
              disabled
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              multiline='true'
              rows={2}
              rowsMax={4}
              label="Observação"
              {...getFieldProps('observacao')}
            />
          </Stack>

          <Stack spacing={3}>
            {status.type === 'erro' ? <Alert severity="error">{status.mensagem}</Alert> : ""}
            {status.type === 'success' ? <Alert severity="success">{status.mensagem}</Alert> : ""}
          </Stack>

          <ThemeProvider theme={Theme}>
            <LoadingButton fullWidth size="large" type="submit" variant="contained" color="neutral" loading={isSubmitting}>
              Cadastrar
            </LoadingButton>
          </ThemeProvider>
        </Stack>  
      </Form>
    </FormikProvider>
  );
}
